import React, { useMemo, useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TimberProject, EnhancedDeal, CRMUser } from '../../types';
import { Search, FolderOpen, Plus, ChevronDown, ChevronRight, ChevronLeft, MoreHorizontal, CheckCircle2, Trash2, HardHat, FileCheck, PenLine, User, SlidersHorizontal, X, Eye, EyeOff, RotateCcw, Calculator } from 'lucide-react';
import { getProjectDisplayNameWithoutNumber, parseLegacyTitle } from '../../utils/projectName';
import { projectSortKey } from '../../utils/projectNumberSort';
import { partitionHubProjects } from '../../utils/hubProjectSections';

interface PMSidebarProps {
  projects: TimberProject[];
  /**
   * Optional companion list of deals — used to surface the canonical project
   * number (e.g. "26-014") and to fall back to the deal's *estimated* value
   * for precon projects that haven't been signed yet (and therefore still
   * carry value=0 on the project record itself).
   */
  deals?: EnhancedDeal[];
  /** Used to resolve project-manager uids to display names in the filter popover. */
  crmUsers?: CRMUser[];
  selectedProjectId: string | null;
  onSelectProject: (id: string | null) => void;
  onCreateProject: () => void;
  onCloseProject?: (id: string) => void;
  onReopenProject?: (id: string) => void;
  onDeleteProject?: (id: string, name: string) => void;
  hideCreate?: boolean;
  hideValues?: boolean;
  onCollapse?: () => void;
  /**
   * Optional handler for the overflow-menu "Rename" entry, available on
   * every row. Parent typically opens the single-deal NameProjectWizard
   * scoped to this project so the operator can edit the client label or
   * descriptor without navigating into the project view.
   */
  onRenameProject?: (project: TimberProject, deal: EnhancedDeal | undefined) => void;
}

const PMSidebar: React.FC<PMSidebarProps> = ({ projects, deals, crmUsers, selectedProjectId, onSelectProject, onCreateProject, onCloseProject, onReopenProject, onDeleteProject, hideCreate, hideValues, onCollapse, onRenameProject }) => {
  const [search, setSearch] = useState('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [showCompleted, setShowCompleted] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // PM + Client multi-select filter (popover anchored next to the search bar).
  // Empty sets = "no filter applied" for that axis.
  const [pmFilter, setPmFilter] = useState<Set<string>>(new Set());
  const [clientFilter, setClientFilter] = useState<Set<string>>(new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  const filterPopRef = useRef<HTMLDivElement>(null);
  const [filterPopCoords, setFilterPopCoords] = useState<{
    left: number;
    top: number;
    maxHeight: number;
  } | null>(null);

  const updateFilterPopPosition = useCallback(() => {
    const btn = filterBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const popW = 260;
    const margin = 8;
    const gap = 6;
    const maxDefault = 480;

    let left = r.right - popW;
    left = Math.max(margin, Math.min(left, window.innerWidth - popW - margin));

    const spaceBelow = window.innerHeight - r.bottom - gap - margin;
    const spaceAbove = r.top - margin - gap;

    let top: number;
    let maxH: number;
    if (spaceBelow >= 180 || spaceBelow >= spaceAbove) {
      top = r.bottom + gap;
      maxH = Math.min(maxDefault, spaceBelow);
    } else {
      maxH = Math.min(maxDefault, spaceAbove);
      top = r.top - gap - maxH;
    }
    if (top < margin) {
      top = margin;
      maxH = Math.min(maxH, window.innerHeight - top - margin);
    }

    setFilterPopCoords({ left, top, maxHeight: Math.max(120, maxH) });
  }, []);

  useLayoutEffect(() => {
    if (!filterOpen) {
      setFilterPopCoords(null);
      return;
    }
    updateFilterPopPosition();
  }, [filterOpen, updateFilterPopPosition]);

  useEffect(() => {
    if (!filterOpen) return;
    const onReposition = () => updateFilterPopPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [filterOpen, updateFilterPopPosition]);

  useEffect(() => {
    if (!menuOpenId) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpenId]);

  useEffect(() => {
    if (!filterOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (filterRef.current?.contains(t)) return;
      if (filterPopRef.current?.contains(t)) return;
      setFilterOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFilterOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [filterOpen]);

  /**
   * Build a fast (id → deal) lookup so per-row deal resolution is O(1) rather
   * than scanning the full deal list inside `renderProjectItem`.
   */
  const dealsById = useMemo(() => {
    const map = new Map<string, EnhancedDeal>();
    if (deals) {
      for (const d of deals) if (d.id) map.set(d.id, d);
    }
    return map;
  }, [deals]);

  const usersByUid = useMemo(() => {
    const map = new Map<string, CRMUser>();
    if (crmUsers) {
      for (const u of crmUsers) if (u.uid) map.set(u.uid, u);
    }
    return map;
  }, [crmUsers]);

  /** Filter popover options mirror the sidebar: no completed projects unless Show Completed is expanded. */
  const filterOptionProjects = useMemo(
    () => (showCompleted ? projects : projects.filter(p => p.status !== 'completed')),
    [projects, showCompleted],
  );

  // Distinct PMs / Clients from `filterOptionProjects` so chips stay aligned with visible rows.
  const pmOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const p of filterOptionProjects) {
      const uid = p.projectManager?.trim();
      if (!uid) continue;
      const name = usersByUid.get(uid)?.displayName?.trim() || 'Unknown';
      seen.set(uid, name);
    }
    return Array.from(seen.entries())
      .map(([uid, name]) => ({ uid, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [filterOptionProjects, usersByUid]);

  const clientOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const p of filterOptionProjects) {
      const deal = p.dealId ? dealsById.get(p.dealId) : undefined;
      const label = (deal?.clientLabel || p.clientName || deal?.contactName || '').trim();
      if (label) seen.add(label);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [filterOptionProjects, dealsById]);

  useEffect(() => {
    const allowedPm = new Set(pmOptions.map(o => o.uid));
    setPmFilter(prev => {
      const next = new Set(Array.from(prev).filter(uid => allowedPm.has(uid)));
      return next.size === prev.size ? prev : next;
    });
  }, [pmOptions]);

  useEffect(() => {
    const allowedClient = new Set(clientOptions);
    setClientFilter(prev => {
      const next = new Set(Array.from(prev).filter(l => allowedClient.has(l)));
      return next.size === prev.size ? prev : next;
    });
  }, [clientOptions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects.filter(p => {
      if (q) {
        const hay = `${p.name} ${p.clientName} ${p.location || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (pmFilter.size > 0) {
        const uid = p.projectManager?.trim() || '';
        if (!pmFilter.has(uid)) return false;
      }
      if (clientFilter.size > 0) {
        const deal = p.dealId ? dealsById.get(p.dealId) : undefined;
        const label = (deal?.clientLabel || p.clientName || deal?.contactName || '').trim();
        if (!clientFilter.has(label)) return false;
      }
      return true;
    });
  }, [projects, search, pmFilter, clientFilter, dealsById]);

  const sortedAscByNumber = useMemo(() =>
    [...filtered].sort((a, b) => {
      const ad = a.dealId ? dealsById.get(a.dealId) : undefined;
      const bd = b.dealId ? dealsById.get(b.dealId) : undefined;
      return projectSortKey(a, ad).localeCompare(projectSortKey(b, bd));
    }),
    [filtered, dealsById]
  );

  const hubSections = useMemo(
    () => partitionHubProjects(sortedAscByNumber),
    [sortedAscByNumber],
  );
  const estimatingProjects = hubSections.estimating;
  const preconProjects = hubSections.preconstruction;
  const contractedActive = hubSections.contracted;
  const allCompleted = hubSections.closed;

  const activeFilterCount = pmFilter.size + clientFilter.size;
  const clearAllFilters = () => {
    setPmFilter(new Set());
    setClientFilter(new Set());
  };
  const togglePm = (uid: string) =>
    setPmFilter(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });
  const toggleClient = (label: string) =>
    setClientFilter(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });

  const toggleSection = (s: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const renderProjectItem = (p: TimberProject) => {
    const deal = p.dealId ? dealsById.get(p.dealId) : undefined;

    // Title row anatomy: <bold project number>  <descriptor (regular weight)>
    //
    // Descriptor sources, in order of trust:
    //   1. deal.descriptor                 — set by the naming wizard
    //   2. parseLegacyTitle(deal.title)    — best-effort split of legacy
    //                                        "First Last - Descriptor"
    //   3. getProjectDisplayNameWithoutNumber(deal) — clientLabel+descriptor
    //                                        fallback for half-migrated rows
    //   4. p.name                          — denormalized last resort
    //
    // We prefer JUST the descriptor in the title since the client name now
    // lives on the meta line below — no point printing it twice.
    const projectNumber = deal?.projectNumber?.trim() || undefined;
    const descriptor =
      (deal?.descriptor?.trim())
      || parseLegacyTitle(deal?.title || '').descriptor
      || getProjectDisplayNameWithoutNumber(deal).trim()
      || p.name
      || '';
    const numberDisplay = projectNumber
      ? (projectNumber.startsWith('#') ? projectNumber : `#${projectNumber}`)
      : null;

    // Client shown on the meta line is the deal's short Client Label
    // (e.g. "Petrus") — the user-curated identifier from the naming wizard.
    // We fall back to the denormalized contact name, then the deal's contact
    // name, only when there's no label yet (legacy / pre-migration rows).
    const clientName = (
      deal?.clientLabel
      || p.clientName
      || deal?.contactName
      || ''
    ).trim();

    // Tooltip combines both lines so hovering on a truncated row reveals
    // the full project number + descriptor + client.
    const titleAttr = [
      numberDisplay,
      descriptor,
      clientName ? `· client: ${clientName}` : '',
    ].filter(Boolean).join(' ').trim();

    // `hideValues` is preserved for API stability with consumers that
    // historically passed it; the value column was removed from the row
    // design (per product direction), so the prop is now a no-op. Reading
    // it here keeps the linter quiet without changing the public surface.
    void hideValues;

    return (
    <div key={p.id} className="pm-sidebar-item-wrap">
      <button
        className={`pm-sidebar-item ${selectedProjectId === p.id ? 'active' : ''}`}
        onClick={() => onSelectProject(p.id!)}
        title={titleAttr}
      >
        <div className="pm-sidebar-project-info">
          <span className="pm-sidebar-project-name">
            {numberDisplay && (
              <span className="pm-sidebar-project-number">{numberDisplay}</span>
            )}
            {descriptor && (
              <span className="pm-sidebar-project-descriptor">{descriptor}</span>
            )}
          </span>
          {clientName && (
            <span className="pm-sidebar-project-meta">
              <User size={10} className="pm-sidebar-meta-icon" aria-hidden />
              <span className="pm-sidebar-meta-label">client</span>
              <span className="pm-sidebar-meta-client">{clientName}</span>
            </span>
          )}
        </div>
      </button>

      {(onCloseProject || onReopenProject || onDeleteProject || onRenameProject) && (
        <div className="pm-sidebar-item-actions" ref={menuOpenId === p.id ? menuRef : undefined}>
          <button
            className="pm-sidebar-overflow-btn"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpenId(menuOpenId === p.id ? null : p.id!);
            }}
          >
            <MoreHorizontal size={14} />
          </button>

          {menuOpenId === p.id && (
            <div className="pm-sidebar-overflow-menu">
              {onRenameProject && (
                <button
                  className="pm-sidebar-overflow-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenId(null);
                    onRenameProject(p, deal);
                  }}
                >
                  <PenLine size={13} />
                  <span>Rename</span>
                </button>
              )}
              {onCloseProject && p.status !== 'completed' && (
                <button
                  className="pm-sidebar-overflow-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenId(null);
                    onCloseProject(p.id!);
                  }}
                >
                  <CheckCircle2 size={13} />
                  <span>Close Out</span>
                </button>
              )}
              {onReopenProject && p.status === 'completed' && (
                <button
                  className="pm-sidebar-overflow-item"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenId(null);
                    onReopenProject(p.id!);
                  }}
                >
                  <RotateCcw size={13} />
                  <span>Reopen</span>
                </button>
              )}
              {onDeleteProject && (
                <button
                  className="pm-sidebar-overflow-item pm-sidebar-overflow-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpenId(null);
                    onDeleteProject(p.id!, p.name);
                  }}
                >
                  <Trash2 size={13} />
                  <span>Delete</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
    );
  };

  return (
    <div className="pm-sidebar pm-scroll">
      <div className="pm-sidebar-header">
        <h3 className="pm-sidebar-title">Projects</h3>
        <div className="pm-sidebar-header-actions">
          {onCollapse && (
            <button
              type="button"
              className="pm-sidebar-collapse-btn"
              onClick={onCollapse}
              aria-label="Collapse projects sidebar"
            >
              <ChevronLeft size={18} strokeWidth={2} aria-hidden />
            </button>
          )}
          {!hideCreate && (
            <button className="pm-btn pm-btn-primary pm-btn-sm" onClick={onCreateProject}>
              <Plus size={12} />
            </button>
          )}
        </div>
      </div>

      <div className="pm-sidebar-search-row">
        <div className="pm-sidebar-search">
          <Search size={14} />
          <input
            placeholder="Search projects..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="pm-sidebar-filter-wrap" ref={filterRef}>
          <button
            ref={filterBtnRef}
            type="button"
            className={`pm-sidebar-filter-btn ${activeFilterCount > 0 ? 'has-active' : ''}`}
            onClick={() => setFilterOpen(o => !o)}
            aria-haspopup="dialog"
            aria-expanded={filterOpen}
            aria-label={`Filter projects${activeFilterCount > 0 ? ` (${activeFilterCount} active)` : ''}`}
          >
            <SlidersHorizontal size={14} aria-hidden />
            {activeFilterCount > 0 && (
              <span className="pm-sidebar-filter-badge">{activeFilterCount}</span>
            )}
          </button>
        </div>
      </div>

      {filterOpen &&
        filterPopCoords &&
        createPortal(
          <div
            ref={filterPopRef}
            className="pm-sidebar-filter-pop pm-sidebar-filter-pop--portal"
            style={{
              left: filterPopCoords.left,
              top: filterPopCoords.top,
              maxHeight: filterPopCoords.maxHeight,
            }}
            role="dialog"
            aria-label="Filter projects"
          >
            <header className="pm-sidebar-filter-pop-head">
              <span>Filter</span>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  className="pm-sidebar-filter-clear"
                  onClick={clearAllFilters}
                >
                  <X size={11} aria-hidden /> Clear
                </button>
              )}
            </header>

            <div className="pm-sidebar-filter-group">
              <span className="pm-sidebar-filter-group-label">Project Manager</span>
              {pmOptions.length === 0 ? (
                <span className="pm-sidebar-filter-empty">No PMs assigned yet</span>
              ) : (
                <div className="pm-sidebar-filter-chips">
                  {pmOptions.map(({ uid, name }) => {
                    const on = pmFilter.has(uid);
                    return (
                      <button
                        key={uid}
                        type="button"
                        className={`pm-sidebar-filter-chip ${on ? 'on' : ''}`}
                        onClick={() => togglePm(uid)}
                        aria-pressed={on}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="pm-sidebar-filter-group">
              <span className="pm-sidebar-filter-group-label">Client</span>
              {clientOptions.length === 0 ? (
                <span className="pm-sidebar-filter-empty">No clients yet</span>
              ) : (
                <div className="pm-sidebar-filter-chips">
                  {clientOptions.map(label => {
                    const on = clientFilter.has(label);
                    return (
                      <button
                        key={label}
                        type="button"
                        className={`pm-sidebar-filter-chip ${on ? 'on' : ''}`}
                        onClick={() => toggleClient(label)}
                        aria-pressed={on}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}

      {/* All Projects — layout matches section headers (chevron slot + icon + label + count) */}
      <button
        type="button"
        className={`pm-sidebar-item pm-sidebar-all ${selectedProjectId === null ? 'active' : ''}`}
        onClick={() => onSelectProject(null)}
      >
        <span className="pm-sidebar-all-chevron-slot" aria-hidden="true" />
        <FolderOpen size={13} className="pm-sidebar-section-icon" aria-hidden />
        <span className="pm-sidebar-all-label">All Projects</span>
        <span className="pm-sidebar-count">{filtered.length}</span>
      </button>

      {/* ═══ Estimating Section ═══ */}
      {estimatingProjects.length > 0 && (
        <div className="pm-sidebar-section">
          <button
            className="pm-sidebar-section-header pm-sidebar-section-header--estimating"
            onClick={() => toggleSection('estimating')}
            aria-expanded={!collapsedSections.has('estimating')}
          >
            {collapsedSections.has('estimating') ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
            <Calculator size={13} className="pm-sidebar-section-icon" aria-hidden />
            <span className="pm-sidebar-section-label">Estimating</span>
            <span className="pm-sidebar-count">{estimatingProjects.length}</span>
          </button>
          {!collapsedSections.has('estimating') && (
            <div className="pm-sidebar-groups">
              {estimatingProjects.map(renderProjectItem)}
            </div>
          )}
        </div>
      )}

      {/* ═══ Preconstruction Section ═══ */}
      {preconProjects.length > 0 && (
        <div className="pm-sidebar-section">
          <button
            className="pm-sidebar-section-header pm-sidebar-section-header--precon"
            onClick={() => toggleSection('precon')}
            aria-expanded={!collapsedSections.has('precon')}
          >
            {collapsedSections.has('precon') ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
            <HardHat size={13} className="pm-sidebar-section-icon" aria-hidden />
            <span className="pm-sidebar-section-label">Preconstruction</span>
            <span className="pm-sidebar-count">{preconProjects.length}</span>
          </button>
          {!collapsedSections.has('precon') && (
            <div className="pm-sidebar-groups">
              {preconProjects.map(renderProjectItem)}
            </div>
          )}
        </div>
      )}

      {/* ═══ Under Contract Section ═══ */}
      {contractedActive.length > 0 && (
        <div className="pm-sidebar-section">
          <button
            className="pm-sidebar-section-header pm-sidebar-section-header--contract"
            onClick={() => toggleSection('contract')}
            aria-expanded={!collapsedSections.has('contract')}
          >
            {collapsedSections.has('contract') ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
            <FileCheck size={13} className="pm-sidebar-section-icon" aria-hidden />
            <span className="pm-sidebar-section-label">Under Contract</span>
            <span className="pm-sidebar-count">{contractedActive.length}</span>
          </button>
          {!collapsedSections.has('contract') && (
            <div className="pm-sidebar-groups">
              {contractedActive.map(renderProjectItem)}
            </div>
          )}
        </div>
      )}

      {/* ═══ Completed Section (peer of the others) ═══ */}
      {allCompleted.length > 0 && (
        <div className="pm-sidebar-section">
          <button
            className="pm-sidebar-section-header pm-sidebar-section-header--completed"
            onClick={() => setShowCompleted(v => !v)}
            aria-expanded={showCompleted}
          >
            {showCompleted ? <EyeOff size={12} /> : <Eye size={12} />}
            <CheckCircle2 size={13} className="pm-sidebar-section-icon" aria-hidden />
            <span className="pm-sidebar-section-label">{showCompleted ? 'Hide' : 'Show'} Completed</span>
            <span className="pm-sidebar-count">{allCompleted.length}</span>
          </button>
          {showCompleted && (
            <div className="pm-sidebar-groups">
              {allCompleted.map(renderProjectItem)}
            </div>
          )}
        </div>
      )}

      {filtered.length === 0 && activeFilterCount > 0 && (
        <div className="pm-sidebar-empty">
          No projects match the current filter.
        </div>
      )}
    </div>
  );
};

export default PMSidebar;
